import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Textarea } from '../../components/ui/FormField.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { ApiError, api } from '../../services/api.js';

/**
 * 421 (#1528, mock-up frame 2): a late extension the customer pays by CARii's QR. The booking read carries the rental's
 * one such leg (`late_payment`); staff approve the receipt, or reject it with a reason the customer reads and may then
 * upload another. Who may decide is the platform's question ("change past bookings"): a refusal shows its sentence.
 */
export interface LatePaymentBlock {
  booking_id: string;
  amount_minor: number;
  status: string;
  late_payment: {
    kind: 'qr';
    receipt_status: 'under_review' | 'approved' | 'rejected' | null;
    receipt_uploaded_at?: string | null;
    receipt_reject_reason?: string | null;
  };
  receipt?: { url: string; mime: string } | null;
}

interface Leg {
  id: string;
  pickup_at: string;
  return_at: string;
}

const label = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const clock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const tidy = (s: string) => s.replace(/\s?(am|pm)$/i, ' $1');
const when = (iso: string) => tidy(label.format(new Date(iso)));
const rm = (minor: number) => `RM ${(minor / 100).toFixed(2)}`;

export function LatePaymentCard({
  latePayment,
  legs,
}: {
  latePayment: LatePaymentBlock;
  legs: Leg[];
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const leg = legs.find((l) => l.id === latePayment.booking_id);
  const lp = latePayment.late_payment;
  const owed = latePayment.status === 'awaiting_payment';

  const decide = useSaveMutation<{ decision: 'approve' | 'reject'; reason?: string }, unknown>({
    resource: 'booking',
    mutationFn: ({ decision, reason: r }) =>
      api.post(
        `/bookings/${latePayment.booking_id}/late-payment/receipt/${decision}`,
        decision === 'reject' ? { reason: r } : {},
      ),
    onSaved: () => {
      setError('');
      setReason('');
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError || err instanceof Error ? err.message : 'The decision failed.',
      );
    },
  });

  function onReject() {
    const r = reason.trim();
    if (!r) {
      setError('Give a reason the customer will read.');
      return;
    }
    void decide.save({ decision: 'reject', reason: r });
  }

  return (
    <Card>
      <div data-testid="late-payment-card" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink">Late extension · payment by QR</h2>
        <p className="text-sm text-steel">
          {rm(latePayment.amount_minor)} {owed ? 'owed' : 'paid'} for{' '}
          {leg
            ? `${when(leg.pickup_at)} – ${tidy(clock.format(new Date(leg.return_at)))}`
            : 'the extra time'}
          .
          {lp.receipt_status === 'under_review' && lp.receipt_uploaded_at
            ? ` Receipt uploaded ${when(lp.receipt_uploaded_at)}.`
            : ''}
        </p>
        {!owed || lp.receipt_status === 'approved' ? (
          <p className="text-sm text-ink">Paid by QR: the receipt was approved.</p>
        ) : lp.receipt_status === 'under_review' ? (
          <>
            {latePayment.receipt ? (
              <a
                href={latePayment.receipt.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary underline"
              >
                Open the receipt
              </a>
            ) : (
              <p className="text-sm text-semantic-danger-text">
                The receipt file could not be found.
              </p>
            )}
            <FormField
              label="Reason (to reject)"
              name="late-reject-reason"
              hint="Required only to reject. The customer sees it and may upload another."
            >
              <Textarea
                id="late-reject-reason"
                maxLength={300}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormField>
            {error && (
              <p data-testid="late-payment-error" className="text-sm text-semantic-danger-text">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onReject} disabled={decide.isPending}>
                Reject
              </Button>
              <Button
                onClick={() => void decide.save({ decision: 'approve' })}
                disabled={decide.isPending}
              >
                Approve payment
              </Button>
            </div>
            <p className="text-xs text-steel">
              Approving marks the extension paid at {rm(latePayment.amount_minor)}, by QR. Only
              staff allowed to change past bookings can approve or reject.
            </p>
          </>
        ) : lp.receipt_status === 'rejected' ? (
          <p className="text-sm text-steel">
            Receipt rejected: {lp.receipt_reject_reason ?? 'no reason given'}. Waiting for the
            customer to upload another.
          </p>
        ) : (
          <p className="text-sm text-steel">
            Waiting for the customer to pay by QR and upload the receipt.
          </p>
        )}
      </div>
    </Card>
  );
}
